const WISHLIST_SESSION_KEY = 'wishlist-fly-animation-seen';
const WISHLIST_TARGET_SELECTOR = '[data-wishlist-target="header-icon"]';

/** Max size of flying thumbnail (px on longest side). */
const FLY_THUMB_MAX = 72;
const FLY_THUMB_MIN = 48;

export type WishlistFlyMode = 'firstAddPerSession' | 'everyAdd';

/** Run on every successful add (see plan Option B). */
export const WISHLIST_FLY_MODE: WishlistFlyMode = 'everyAdd';

function hasWindowContext(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function userPrefersReducedMotion(): boolean {
    if (!hasWindowContext() || typeof window.matchMedia !== 'function') {
        return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isRenderableRect(rect: DOMRect): boolean {
    return rect.width > 0 && rect.height > 0 && rect.bottom >= 0 && rect.right >= 0;
}

function hasPlayedInSession(): boolean {
    if (!hasWindowContext()) {
        return false;
    }
    try {
        return window.sessionStorage.getItem(WISHLIST_SESSION_KEY) === '1';
    } catch {
        return false;
    }
}

function markPlayedInSession(): void {
    try {
        window.sessionStorage.setItem(WISHLIST_SESSION_KEY, '1');
    } catch {
        // Ignore storage access failures; animation can still run.
    }
}

/**
 * Prefer product image near the wishlist button so the flight reads clearly (not a tiny heart chip).
 */
function findProductImageForFly(originElement: HTMLElement): HTMLImageElement | null {
    const media = originElement.closest('.pl-item__media');
    if (media) {
        const img = media.querySelector<HTMLImageElement>('img');
        if (img && (img.currentSrc || img.src)) {
            return img;
        }
    }
    const wrap = originElement.closest('.po__left__image-wrap');
    if (wrap) {
        const img =
            wrap.querySelector<HTMLImageElement>('img.po__left__image') ||
            wrap.querySelector<HTMLImageElement>('img');
        if (img && (img.currentSrc || img.src)) {
            return img;
        }
    }
    return null;
}

function buildThumbnailGhost(img: HTMLImageElement): { ghost: HTMLElement; startRect: DOMRect } {
    const startRect = img.getBoundingClientRect();
    const src = img.currentSrc || img.src;
    const ghost = document.createElement('div');
    ghost.className = 'wishlist-fly-ghost wishlist-fly-ghost--thumb';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.setAttribute('tabindex', '-1');

    const inner = document.createElement('img');
    inner.src = src;
    inner.alt = '';
    inner.decoding = 'async';
    inner.draggable = false;
    ghost.appendChild(inner);

    const w = startRect.width;
    const h = startRect.height;
    const longSide = Math.max(w, h);
    let scale = longSide > 0 ? Math.min(FLY_THUMB_MAX / longSide, 1) : 1;
    if (longSide * scale < FLY_THUMB_MIN && longSide > 0) {
        scale = Math.min(FLY_THUMB_MIN / longSide, 2);
    }
    const displayW = Math.max(FLY_THUMB_MIN * 0.5, w * scale);
    const displayH = Math.max(FLY_THUMB_MIN * 0.5, h * scale);
    ghost.style.width = `${Math.round(displayW)}px`;
    ghost.style.height = `${Math.round(displayH)}px`;

    return { ghost, startRect };
}

function buildButtonCloneGhost(originElement: HTMLElement): { ghost: HTMLElement; startRect: DOMRect } {
    const startRect = originElement.getBoundingClientRect();
    const ghost = originElement.cloneNode(true) as HTMLElement;
    ghost.className = 'wishlist-fly-ghost wishlist-fly-ghost--button';
    ghost.removeAttribute('id');
    ghost.removeAttribute('disabled');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.setAttribute('tabindex', '-1');
    return { ghost, startRect };
}

export function triggerWishlistFlyAnimation(
    originElement: HTMLElement | null,
    mode: WishlistFlyMode = WISHLIST_FLY_MODE
): boolean {
    if (!hasWindowContext() || !originElement || userPrefersReducedMotion()) {
        return false;
    }

    if (mode === 'firstAddPerSession' && hasPlayedInSession()) {
        return false;
    }

    const targetElement = document.querySelector<HTMLElement>(WISHLIST_TARGET_SELECTOR);
    if (!targetElement) {
        return false;
    }

    const productImg = findProductImageForFly(originElement);
    const { ghost, startRect } = productImg
        ? buildThumbnailGhost(productImg)
        : buildButtonCloneGhost(originElement);

    if (!isRenderableRect(startRect)) {
        return false;
    }

    const startX = startRect.left + startRect.width / 2;
    const startY = startRect.top + startRect.height / 2;

    ghost.style.left = `${startX}px`;
    ghost.style.top = `${startY}px`;
    document.body.appendChild(ghost);

    const startFlight = () => {
        const endRect = targetElement.getBoundingClientRect();
        if (!isRenderableRect(endRect)) {
            ghost.remove();
            return;
        }

        const endX = endRect.left + endRect.width / 2;
        const endY = endRect.top + endRect.height / 2;
        const dx = endX - startX;
        const dy = endY - startY;
        const midpointX = dx * 0.55;
        const midpointY = dy * 0.28 - Math.max(50, Math.min(160, Math.abs(dy) * 0.4));
        const distance = Math.hypot(dx, dy);
        const duration = Math.round(Math.min(1300, Math.max(720, distance * 1.05)));

        if (typeof ghost.animate !== 'function') {
            ghost.remove();
            return;
        }

        const animation = ghost.animate(
            [
                {
                    transform: 'translate(-50%, -50%) translate(0px, 0px) scale(1)',
                    opacity: 1,
                },
                {
                    transform: `translate(-50%, -50%) translate(${midpointX}px, ${midpointY}px) scale(0.92)`,
                    opacity: 0.95,
                    offset: 0.55,
                },
                {
                    transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(0.42)`,
                    opacity: 0.15,
                },
            ],
            {
                duration,
                easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
                fill: 'forwards',
            }
        );

        targetElement.classList.add('wishlist-fly-target-bump');
        window.setTimeout(() => {
            targetElement.classList.remove('wishlist-fly-target-bump');
        }, 380);

        animation.onfinish = () => {
            ghost.remove();
        };
        animation.oncancel = () => {
            ghost.remove();
        };
    };

    requestAnimationFrame(() => {
        requestAnimationFrame(startFlight);
    });

    if (mode === 'firstAddPerSession') {
        markPlayedInSession();
    }

    return true;
}
